import { FC } from 'react';
import { Modal, Typography } from '@crisellevaldez/speculo-ui';
import { CallDetailsPanel } from '../contacts/CallDetailsPanel';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import { Calendar } from 'lucide-react';
import { formatDate } from '../../../utils/dateTime';
import { AnsweredBy } from '../../../utils/badgeStyles';
import { type DateInput } from '../../../utils/dateTime';
import type { GQLTransferSummary } from '../../../hooks/graphql/dashboard/useOrgLiveTransfersGQL';

export interface CallRecord {
  id: string;
  call_id: string;
  answered_by?: AnsweredBy;
  callTimestamp?: DateInput;
  call_analysis?: {
    outcome: string;
    notes?: string;
    timeline?: Array<{ event: string; timestamp: string }>;
    answers?: {
      'Willingness to Speak with an Agent'?: string;
      'Transaction Type'?: string;
      Motivation?: string;
      [key: string]: string | undefined;
    };
    summary?: string;
  };
  duration?: number;
  corrected_duration?: number;
  concatenated_transcript?: string;
  inbound?: boolean;
  recordingUrl?: string;
  flowName?: string;
  flowType?: string;
  contact?: {
    firstName?: string;
    lastName?: string;
    [key: string]: unknown;
  };
  transfer?: GQLTransferSummary | null;
}

interface CallDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  call: CallRecord | null;
  /** Render the modal shell with a loading skeleton when the caller
   *  opens it before the call record has finished fetching. Without
   *  this, callers had to await the fetch and then setOpen, which
   *  read as a 200–400ms click-to-modal lag on the hot-leads feed. */
  isLoading?: boolean;
}

export const CallDetailsModal: FC<CallDetailsModalProps> = ({
  isOpen,
  onClose,
  call,
  isLoading = false,
}) => {
  const organization = useOrganizationStore((state) => state.organization);
  const orgTimezone = organization?.timezone || 'UTC';

  // Open the shell as soon as the caller flips `isOpen=true`, even if
  // `call` hasn't loaded yet. Falls back to `null` only when neither
  // condition holds.
  if (!isOpen) return null;
  if (!call && !isLoading) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Header>
        <div>
          <div className="flex flex-col lg:flex-row lg:items-center gap-1 lg:gap-4">
            <Typography variant="h3">Call Details</Typography>
            {call?.callTimestamp && (
              <div className="flex items-center gap-2 text-sm text-muted-foreground font-normal">
                <span>
                  <Calendar className="h-4 w-4 inline-block" />{' '}
                  {formatDate(call.callTimestamp, orgTimezone, 'MMM D, YYYY h:mm A')}
                </span>
              </div>
            )}
          </div>
        </div>
      </Modal.Header>
      <Modal.Body className="overflow-hidden">
        <div className="h-[calc(100vh-200px)] overflow-y-auto">
          {call ? (
            <CallDetailsPanel
              call={call}
              orgTimezone={orgTimezone}
              flowInfo={
                call.flowName && call.flowName !== '-'
                  ? {
                      name: call.flowName,
                      type: call.flowType || '-',
                    }
                  : undefined
              }
              contactFirstName={call.contact?.firstName}
              assistantName={organization?.assistant_name}
            />
          ) : (
            // Skeleton matches the shape CallDetailsPanel will render
            // (header block + body panels) so the swap-in feels like
            // content filling in, not a layout shift.
            <div className="flex flex-col gap-4 p-4 animate-pulse" aria-hidden>
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-full bg-foreground/10" />
                <div className="flex flex-col gap-2 flex-1">
                  <div className="h-3 w-1/3 rounded bg-foreground/10" />
                  <div className="h-3 w-1/2 rounded bg-foreground/10" />
                </div>
              </div>
              <div className="h-24 w-full rounded-lg bg-foreground/10" />
              <div className="h-32 w-full rounded-lg bg-foreground/10" />
              <div className="h-24 w-full rounded-lg bg-foreground/10" />
            </div>
          )}
        </div>
      </Modal.Body>
    </Modal>
  );
};
